import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Toaster } from "sonner";

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const link = (to, label, tid) => (
    <Link
      key={to}
      to={to}
      data-testid={tid}
      onClick={() => setOpen(false)}
      className={`text-sm tracking-tight hover:text-[color:var(--brand-terracotta)] transition-colors ${loc.pathname === to ? "text-[color:var(--brand-terracotta)] font-semibold" : "text-stone-700 dark:text-stone-300"}`}
    >
      {label}
    </Link>
  );
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster position="top-right" richColors />
      <header className="border-b border-border sticky top-0 z-40 backdrop-blur bg-background/85">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[color:var(--brand-terracotta)] flex items-center justify-center text-white font-display font-black text-sm tracking-tighter">CGC</div>
            <div className="leading-tight">
              <div className="font-display font-bold text-base tracking-tight lowercase">register | cgc india</div>
              <div className="text-[10px] font-mono tracking-widest uppercase text-stone-500">We Think. We Act.</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {link("/", "Home", "nav-home")}
            {link("/events", "Events", "nav-events")}
            {link("/impact", "Impact", "nav-impact")}
            {link("/portal", "My tickets", "nav-portal")}
            {link("/help", "Help", "nav-help")}
          </nav>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-border bg-background px-4 py-4 flex flex-col gap-4">
            {link("/", "Home", "nav-home-m")}
            {link("/events", "Events", "nav-events-m")}
            {link("/impact", "Impact", "nav-impact-m")}
            {link("/portal", "My tickets", "nav-portal-m")}
            {link("/help", "Help", "nav-help-m")}
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-border mt-24 bg-stone-50/50 dark:bg-stone-950/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="font-display text-2xl font-bold">register | cgc india</div>
            <div className="eyebrow mt-1">We Think. We Act.</div>
            <p className="mt-4 text-stone-600 dark:text-stone-400 max-w-md text-sm leading-relaxed">
              Official ticketing and registration platform for Common Ground Collective India. Fundraisers, workshops, artisan pop-ups, and community gatherings.
            </p>
            <div className="mt-4 text-xs text-stone-500 space-y-1">
              <p><b>Legal Entity:</b> Common Ground Collective India</p>
              <p><b>Location:</b> Visakhapatnam, Andhra Pradesh, India</p>
              <p><b>Support:</b> <a href="mailto:office@cgcollective.in" className="text-[color:var(--brand-terracotta)] hover:underline">office@cgcollective.in</a></p>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">Platform & Help</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/events" className="hover:text-[color:var(--brand-terracotta)]">All events</Link></li>
              <li><Link to="/portal" className="hover:text-[color:var(--brand-terracotta)]">Attendee Portal</Link></li>
              <li><Link to="/contact" className="hover:text-[color:var(--brand-terracotta)]">Contact Us</Link></li>
              <li><Link to="/help" className="hover:text-[color:var(--brand-terracotta)]">Help & FAQ</Link></li>
              <li><a href="https://cgcollective.in" target="_blank" rel="noreferrer" className="hover:text-[color:var(--brand-terracotta)]">Main Website ↗</a></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-3">Policies & Compliance</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/terms" className="hover:text-[color:var(--brand-terracotta)]">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-[color:var(--brand-terracotta)]">Privacy Policy</Link></li>
              <li><Link to="/refund-policy" className="hover:text-[color:var(--brand-terracotta)]">Cancellation & Refund Policy</Link></li>
              <li><Link to="/shipping" className="hover:text-[color:var(--brand-terracotta)]">Shipping & Delivery Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-stone-500 flex flex-wrap gap-4 items-center justify-between">
            <span>© {new Date().getFullYear()} Common Ground Collective India. All rights reserved.</span>
            <span className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
              <span>100% Secure Payments Powered by <b>Razorpay</b></span>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
